import { toRelativeAppPath } from "./kakaoOidc";
import { ENGLISH_APP_ORIGIN, KOREAN_APP_ORIGIN } from "../i18n/locale";

const KAKAO_AUTOSTART_PARAM = "kakao_autostart";
const KAKAO_NEXT_PARAM = "kakao_next";
const NOTIFICATION_BROWSER_ATTEMPT_PARAM = "notification_browser_attempted";
export const KAKAO_EXTERNAL_BROWSER_AUTO_OPEN_PARAM = "kakao_open_browser";
export const KAKAO_EXTERNAL_BROWSER_PROMPT_PATH = "/auth/kakao/browser";

type BuildKakaoExternalBrowserTargetUrlParams = {
  currentUrl: string;
  next?: string | null;
};

export function buildKakaoExternalBrowserTargetUrl({
  currentUrl,
  next,
}: BuildKakaoExternalBrowserTargetUrlParams) {
  const url = new URL(currentUrl);
  url.searchParams.set(KAKAO_AUTOSTART_PARAM, "1");
  url.searchParams.set(KAKAO_NEXT_PARAM, toRelativeAppPath(next));
  return url.toString();
}

export function buildKakaoExternalBrowserPromptUrl(next?: string | null, autoOpen = false) {
  const params = new URLSearchParams({ next: toRelativeAppPath(next) });
  if (autoOpen) {
    params.set(KAKAO_EXTERNAL_BROWSER_AUTO_OPEN_PARAM, "1");
  }
  return `${KAKAO_EXTERNAL_BROWSER_PROMPT_PATH}?${params.toString()}`;
}

export function buildKakaoExternalBrowserPromptAbsoluteUrl(next?: string | null, currentHref?: string) {
  const baseHref = currentHref ?? (typeof window !== "undefined" ? window.location.href : "https://mypetroutine.petnow.io/");
  return new URL(buildKakaoExternalBrowserPromptUrl(next), baseHref).toString();
}

export function buildAndroidExternalBrowserUrl(targetUrl: string) {
  const url = new URL(targetUrl);
  const scheme = url.protocol.replace(":", "");
  const fallbackUrl = encodeURIComponent(url.toString());
  return `intent://${url.host}${url.pathname}${url.search}${url.hash}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${fallbackUrl};end`;
}

export function buildNotificationExternalBrowserTargetUrl(currentUrl: string) {
  const url = new URL(currentUrl);
  url.searchParams.set(NOTIFICATION_BROWSER_ATTEMPT_PARAM, "1");
  return url.toString();
}

function isRoutineNotificationEntry(url: URL) {
  const searchParams = url.searchParams;
  return url.pathname.startsWith("/app")
    && (
      searchParams.get("from") === "notification"
      || searchParams.get("routineAction") === "complete"
    );
}

/**
 * Routine notification actions always use the Korean public auth origin.
 *
 * A notification link can be reopened by an existing English-host browser
 * session (for example after an in-app browser handoff). Keeping the action on
 * that host selects the English auth policy, which deliberately does not
 * start Kakao login. Ordinary English pages remain on their original host.
 */
export function buildKoreanNotificationEntryUrl(currentUrl: string) {
  const url = new URL(currentUrl);
  if (url.origin !== ENGLISH_APP_ORIGIN || !isRoutineNotificationEntry(url)) {
    return url.toString();
  }

  return new URL(`${url.pathname}${url.search}${url.hash}`, KOREAN_APP_ORIGIN).toString();
}

export function shouldHandoffNotificationEntryToExternalBrowser(params: {
  pathname: string;
  search: string;
  runtime: {
    isAndroid: boolean;
    isKakaoTalkInAppBrowser?: boolean;
  };
}) {
  const searchParams = new URLSearchParams(params.search);
  const isNotificationEntry =
    params.pathname.startsWith("/app")
    && (
      searchParams.get("from") === "notification"
      || searchParams.get("routineAction") === "complete"
    );

  return isNotificationEntry
    && params.runtime.isAndroid
    && params.runtime.isKakaoTalkInAppBrowser === true
    && searchParams.get(NOTIFICATION_BROWSER_ATTEMPT_PARAM) !== "1";
}

export function readKakaoAutoStartSearchParams(search: string) {
  const params = new URLSearchParams(search);
  return {
    next: toRelativeAppPath(params.get(KAKAO_NEXT_PARAM)),
    shouldAutoStart: params.get(KAKAO_AUTOSTART_PARAM) === "1",
  };
}

export function clearKakaoAutoStartSearchParams(search: string) {
  const params = new URLSearchParams(search);
  params.delete(KAKAO_AUTOSTART_PARAM);
  params.delete(KAKAO_NEXT_PARAM);
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
}
